import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { type KeyboardEvent, useId, useRef, useState } from "react";
import { Button } from "@/core/components/ui/button";
import { Checkbox } from "@/core/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { SimpleInput } from "@/core/components/ui/simple-input";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";

export interface MultiSelectOption {
  id: string;
  name: string;
  disabled?: boolean;
}

interface MultiSelectProps {
  label: string;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  variant?: "field" | "filter";
}

/** A checkbox picker with the same chip and menu treatment as roster filters. */
export const MultiSelect = ({
  label,
  options,
  value,
  onChange,
  disabled,
  loading,
  placeholder = "Select",
  variant = "field",
}: MultiSelectProps) => {
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const isFilter = variant === "filter";
  const selected = options.filter((option) => value.includes(option.id));
  const active = selected.length > 0;
  const query = search.trim().toLocaleLowerCase();
  const visible = options.filter((option) =>
    option.name.toLocaleLowerCase().includes(query),
  );
  const toggle = (id: string) =>
    onChange(
      value.includes(id) ? value.filter((item) => item !== id) : [...value, id],
    );
  const handleArrowKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const inputs = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="checkbox"]:not(:disabled)',
      ) ?? [],
    );
    if (!inputs.length) return;
    const current = inputs.indexOf(document.activeElement as HTMLButtonElement);
    const direction = event.key === "ArrowDown" ? 1 : -1;
    const next =
      current === -1
        ? direction > 0
          ? 0
          : inputs.length - 1
        : (current + direction + inputs.length) % inputs.length;
    inputs[next]?.focus();
  };

  return (
    <Stack gap="xs" className="min-w-0" align="stretch">
      {!isFilter && (
        <span id={labelId} className="text-sm font-medium">
          {label}
        </span>
      )}
      <Popover
        onOpenChange={(open) => {
          if (!open) setSearch("");
        }}
      >
        <Stack
          direction="row"
          align="center"
          gap={isFilter ? "none" : "xs"}
          className={cn("min-w-0", {
            "h-9 shrink-0 rounded-full border transition-colors": isFilter,
            "border-stroke-interactive bg-green-5": isFilter && active,
            "border-stroke-secondary bg-surface-primary hover:border-stroke-primary":
              isFilter && !active,
          })}
        >
          <PopoverTrigger asChild>
            {isFilter ? (
              <button
                type="button"
                disabled={disabled}
                aria-label={`${label}: ${selected.map((item) => item.name).join(", ") || placeholder}`}
                title={
                  active
                    ? selected.map((item) => item.name).join(", ")
                    : undefined
                }
                className={cn(
                  "flex h-9 min-w-0 items-center gap-1.5 rounded-full pl-3 pr-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50",
                  { "pr-1": active },
                )}
              >
                <span className="shrink-0 text-secondary-text">{label}</span>
                {active ? (
                  <span className="max-w-[13rem] truncate font-medium text-primary-text">
                    {selected.length === 1
                      ? selected[0].name
                      : `${selected.length} selected`}
                  </span>
                ) : (
                  <ChevronDownIcon className="size-3.5 shrink-0 text-tertiary-text" />
                )}
              </button>
            ) : (
              <Button
                type="button"
                variant="tertiary"
                size="xs"
                disabled={disabled}
                aria-label={`${label}: ${selected.map((item) => item.name).join(", ") || placeholder}`}
                className="min-w-0 flex-1 justify-between"
              >
                <span className="truncate">
                  {selected[0]?.name ?? placeholder}
                </span>
                {selected.length > 1 && <span>+{selected.length - 1}</span>}
                <ChevronDownIcon aria-hidden className="size-4" />
              </Button>
            )}
          </PopoverTrigger>
          {active &&
            (isFilter ? (
              <button
                type="button"
                aria-label={`Clear ${label} filter`}
                disabled={disabled}
                onClick={() => onChange([])}
                className="mr-1.5 flex size-5 shrink-0 items-center justify-center rounded-full text-secondary-text transition-colors hover:bg-surface-secondary hover:text-primary-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <XMarkIcon className="size-3.5" />
              </button>
            ) : (
              <Button
                type="button"
                size="iconSm"
                variant="text"
                disabled={disabled}
                aria-label={`Remove ${selected[0].name}`}
                onClick={() => toggle(selected[0].id)}
              >
                <XMarkIcon aria-hidden />
              </Button>
            ))}
        </Stack>
        <PopoverContent align="start" className="w-72 p-0">
          <Stack
            direction="row"
            align="center"
            justify="between"
            gap="sm"
            fullWidth
            className="border-b border-stroke-tertiary px-3 py-2"
          >
            <Typography variant="subtitleW3">{label}</Typography>
            {value.length > 0 && (
              <Button
                type="button"
                size="xs"
                variant="text"
                onClick={() => onChange([])}
              >
                Clear all
              </Button>
            )}
          </Stack>
          <Stack
            gap="none"
            fullWidth
            className="border-b border-stroke-tertiary px-3 py-2 [&>div]:w-full"
          >
            <SimpleInput
              aria-label={`Search ${label}`}
              placeholder="Search values"
              value={search}
              startAdornment={<MagnifyingGlassIcon className="size-4" />}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleArrowKey}
              className="h-9 text-sm placeholder:text-sm placeholder:leading-5 md:text-sm"
            />
          </Stack>
          <Stack
            ref={listRef}
            role="group"
            aria-label={label}
            gap="none"
            fullWidth
            className="max-h-64 overflow-y-auto p-1"
            onKeyDown={handleArrowKey}
          >
            {loading ? (
              <span role="status" className="px-3 py-4 text-sm">
                Loading...
              </span>
            ) : visible.length === 0 ? (
              <span
                role="status"
                className="px-3 py-4 text-sm text-secondary-text"
              >
                No options found
              </span>
            ) : (
              visible.map((option) => (
                <label
                  key={option.id}
                  htmlFor={`${labelId}-${option.id}`}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
                    {
                      "cursor-pointer hover:bg-accent": !option.disabled,
                      "opacity-50": option.disabled,
                    },
                  )}
                >
                  <Checkbox
                    id={`${labelId}-${option.id}`}
                    checked={value.includes(option.id)}
                    disabled={option.disabled}
                    onCheckedChange={() => toggle(option.id)}
                  />
                  <span
                    className="min-w-0 flex-1 truncate text-sm text-primary-text"
                    title={option.name}
                  >
                    {option.name}
                  </span>
                </label>
              ))
            )}
          </Stack>
        </PopoverContent>
      </Popover>
    </Stack>
  );
};
